import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import ShareButton from '@/components/Post/ShareButton';
import BlogCard from '@/components/Blog/BlogCard';
import useDocumentMeta from '@/customHooks/useDocumentMeta';
import {
  useGetBlogBySlugQuery,
  useGetRelatedBlogsQuery,
} from '@/redux/blog/blog.api.slice';
import { IBlogListItem, IBlogPost } from '@/redux/blog/blog.d';
import './blog-content.css';

const RELATED_LIMIT = 3;

type RefetchBlog = ReturnType<typeof useGetBlogBySlugQuery>['refetch'];

function useSlug(): string {
  const { slug = '' } = useParams<{ slug: string }>();
  return slug;
}

function getContentUrl(post: IBlogPost | undefined): string | undefined {
  return post?.contentHtmlUrl;
}

function formatPublishedAt(date: string | null | undefined): string {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return format(parsed, 'MMMM d, yyyy');
}

function toError(value: unknown, fallback: Error): Error {
  return value instanceof Error ? value : fallback;
}

// Default cache semantics — the object is immutable so browser/edge caching is
// correct here. Do NOT use getPublicFileFromS3 (no-cache).
async function fetchText(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to load blog content (${res.status})`);
  }
  return res.text();
}

// The key may have been replaced by an admin save moments ago. Refetch the
// metadata once and retry with the fresh URL. Rethrows the original error if
// the refresh yields no usable URL.
async function refetchAndRetry(
  refetch: RefetchBlog,
  firstErr: unknown
): Promise<string> {
  const refreshed = await refetch().unwrap();
  const freshUrl = refreshed.contentHtmlUrl;
  if (!freshUrl) throw firstErr;
  return fetchText(freshUrl);
}

type BodyState =
  | { kind: 'loading' }
  | { kind: 'ready'; html: string }
  | { kind: 'error'; error: Error };

interface BlogBody {
  contentHtml: string | null;
  fatalError: Error | null;
}

function useBlogBody(
  contentHtmlUrl: string | undefined,
  refetch: RefetchBlog
): BlogBody {
  const [state, setState] = useState<BodyState>({ kind: 'loading' });

  useEffect(() => {
    if (!contentHtmlUrl) return undefined;

    let cancelled = false;
    const commit = (next: BodyState) => {
      if (!cancelled) setState(next);
    };

    async function loadFallback(firstErr: unknown): Promise<BodyState> {
      try {
        const html = await refetchAndRetry(refetch, firstErr);
        return { kind: 'ready', html };
      } catch (secondErr) {
        const fallback = new Error('Failed to load blog content');
        return { kind: 'error', error: toError(secondErr, fallback) };
      }
    }

    async function loadBody(url: string): Promise<BodyState> {
      try {
        return { kind: 'ready', html: await fetchText(url) };
      } catch (firstErr) {
        return loadFallback(firstErr);
      }
    }

    commit({ kind: 'loading' });
    loadBody(contentHtmlUrl).then(commit);

    return () => {
      cancelled = true;
    };
  }, [contentHtmlUrl, refetch]);

  const contentHtml = state.kind === 'ready' ? state.html : null;
  const fatalError = state.kind === 'error' ? state.error : null;
  return { contentHtml, fatalError };
}

function metaTitle(post: IBlogPost | undefined): string {
  if (!post) return 'Blog | Filefinity';
  const title = post.seoTitle || post.title;
  return `${title} | Filefinity`;
}

function metaDescription(post: IBlogPost | undefined): string | undefined {
  if (!post) return undefined;
  return post.seoDescription || post.excerpt || undefined;
}

function metaImage(post: IBlogPost | undefined): string | undefined {
  if (!post) return undefined;
  return post.coverImageUrl || undefined;
}

function loadErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'status' in error) {
    return `Failed to load blog post (${String(error.status)})`;
  }
  return 'Failed to load blog post';
}

// Surface failures to the route-level ErrorBoundary by throwing during render
// (errorElement only catches render-time errors, not async-callback ones).
function assertNoFatal(
  fatalError: Error | null,
  isError: boolean,
  error: unknown
): void {
  if (fatalError) throw fatalError;
  if (isError) throw new Error(loadErrorMessage(error));
}

function BlogPostSkeleton() {
  return (
    <div
      className="px-4 sm:px-[48px] py-[10px] pb-24"
      style={{ marginTop: 'var(--nav-height)' }}
    >
      <div className="max-w-3xl mx-auto w-full pt-6">
        <Skeleton className="h-10 w-3/4 mb-4" />
        <Skeleton className="h-5 w-1/2 mb-6" />
        <Skeleton className="w-full h-[320px] mb-8" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}

function PostCategoryChip({ post }: { post: IBlogPost }) {
  const category = post.categories?.[0];
  if (!category) return null;
  return (
    <span className="self-start rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary capitalize">
      {category.name}
    </span>
  );
}

function PostByline({ post }: { post: IBlogPost }) {
  const formattedDate = formatPublishedAt(post.publishedAt);
  const dateSuffix = formattedDate ? ` · ${formattedDate}` : '';
  const readSuffix = post.readTimeMinutes
    ? ` · ${post.readTimeMinutes} min read`
    : '';
  return (
    <p className="text-sm text-[#666666] font-secondary">
      By {post.authorName}
      {dateSuffix}
      {readSuffix}
    </p>
  );
}

function PostHeader({ post }: { post: IBlogPost }) {
  return (
    <header className="flex flex-col gap-4 pt-6">
      <PostCategoryChip post={post} />
      <h1 className="text-3xl sm:text-4xl font-bold font-primary leading-tight">
        {post.title}
      </h1>
      <PostByline post={post} />
    </header>
  );
}

function PostCover({ post }: { post: IBlogPost }) {
  if (!post.coverImageUrl) return null;
  return (
    <div className="w-full mt-6 overflow-hidden rounded-lg bg-gray-50">
      <img
        src={post.coverImageUrl}
        alt={post.title}
        className="w-full h-auto object-cover"
        decoding="async"
      />
    </div>
  );
}

function BodySkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}

function PostBody({ contentHtml }: { contentHtml: string | null }) {
  if (contentHtml === null) {
    return (
      <div className="mt-8">
        <BodySkeleton />
      </div>
    );
  }
  return (
    <div className="mt-8">
      {/* Body is server-sanitized (spec §5) before it reaches S3. */}
      <div
        className="blog-content"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: contentHtml }}
      />
    </div>
  );
}

function ShareSection({ post }: { post: IBlogPost }) {
  const shareUrl =
    typeof window !== 'undefined' ? window.location.href : `/blog/${post.slug}`;
  return (
    <div className="mt-10 flex items-center gap-3 border-t border-gray-200 pt-6">
      <span className="text-sm font-medium font-secondary text-gray-700">
        Share this post
      </span>
      <ShareButton
        shareUrl={shareUrl}
        shareTitle={post.title}
        shareText={post.excerpt}
      />
    </div>
  );
}

function RelatedPosts({
  relatedPosts,
}: {
  relatedPosts: IBlogListItem[] | undefined;
}) {
  if (!relatedPosts || relatedPosts.length === 0) return null;

  return (
    <section className="mt-12">
      <h2 className="text-xl font-bold font-primary mb-4">Related posts</h2>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-5">
        {relatedPosts.slice(0, RELATED_LIMIT).map((related) => (
          <BlogCard key={related._id} post={related} />
        ))}
      </div>
    </section>
  );
}

function BlogPostContent({
  post,
  contentHtml,
  relatedPosts,
}: {
  post: IBlogPost;
  contentHtml: string | null;
  relatedPosts: IBlogListItem[] | undefined;
}) {
  return (
    <article
      className="px-4 sm:px-[48px] py-[10px] pb-24"
      style={{ marginTop: 'var(--nav-height)' }}
    >
      <div className="max-w-3xl mx-auto w-full">
        <PostHeader post={post} />
        <PostCover post={post} />
        <PostBody contentHtml={contentHtml} />
        <ShareSection post={post} />
        <RelatedPosts relatedPosts={relatedPosts} />
      </div>
    </article>
  );
}

function BlogPost() {
  const slug = useSlug();

  const {
    data: post,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetBlogBySlugQuery(slug, { skip: !slug });

  const { data: relatedPosts } = useGetRelatedBlogsQuery(
    { slug, limit: RELATED_LIMIT },
    { skip: !slug }
  );

  const { contentHtml, fatalError } = useBlogBody(getContentUrl(post), refetch);

  useDocumentMeta(metaTitle(post), metaDescription(post), metaImage(post));

  assertNoFatal(fatalError, isError, error);

  if (isLoading) return <BlogPostSkeleton />;
  if (!post) return <BlogPostSkeleton />;

  return (
    <BlogPostContent
      post={post}
      contentHtml={contentHtml}
      relatedPosts={relatedPosts}
    />
  );
}

export default BlogPost;
